/**
 * 자르기 판의 기하 — **DOM 없이** 계산한다.
 *
 * 틀은 **판 위에 붙박이**고, 그 밑에서 **사진이 미끄러진다.** 어디를 남길지는
 * 사진을 끌어 틀 안으로 가져와 고른다. 휴대폰 사진 앱·프로필 사진 등록에서
 * 다들 쓰는 손놀림이라 따로 배울 것이 없다.
 *
 * ⚠️ **틀을 움직이지 않는 이유**: 결과가 나올 자리는 늘 같은 데 있어야 한다.
 * 틀이 판 안을 돌아다니면 "지금 남는 게 어디였더라"를 매번 다시 찾아야 한다.
 * 크기만 모서리로 바꾼다.
 *
 * ⚠️ **비율은 잠겨 있다.** 격자 한 칸이 4:5라 그 비율로만 잘라야 한다.
 * 자유롭게 늘리게 두면 결과가 칸에 안 맞아 다시 잘려야 하고, 그러면
 * 사용자가 고른 자리가 또 한 번 흔들린다.
 *
 * 좌표는 전부 **판(stage) 기준**이다. 사진도 틀도 같은 자에 올려 두어야
 * "틀이 사진 안에 있는가"를 뺄셈 한 번으로 본다.
 *
 * 계산만 떼어낸 이유: 손맛의 문제처럼 보이지만 실제로는 산수다. 어긋나면
 * 화면에서 본 자리와 잘려 나온 자리가 다른데, 눌러 보기 전까지 모른다.
 */

export interface Size {
  width: number;
  height: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

/** 잡을 수 있는 네 모서리 */
export type Corner = 'nw' | 'ne' | 'sw' | 'se';

/** 틀이 이보다 작아지면 무엇을 고르는지 안 보인다 */
export const MIN_CROP_WIDTH = 48;

const clamp = (value: number, a: number, b: number): number =>
  Math.min(Math.max(value, Math.min(a, b)), Math.max(a, b));

/**
 * 사진 **전체가 보이는** 배율.
 *
 * 판을 덮는 배율(`cover`)이 아니라 담기는 배율(`contain`)이다 — 처음에
 * 사진이 잘려 있으면 화면 밖에 무엇이 있는지 모르는 채로 자리를 고르게 된다.
 */
export function fitScale(container: Size, image: Size): number {
  if (image.width <= 0 || image.height <= 0) {
    return 1;
  }
  return Math.min(container.width / image.width, container.height / image.height);
}

/** 처음 사진 자리. 판 가운데에 통째로 놓는다 */
export function initialPhoto(stage: Size, shown: Size): Rect {
  return {
    x: (stage.width - shown.width) / 2,
    y: (stage.height - shown.height) / 2,
    width: shown.width,
    height: shown.height,
  };
}

/**
 * 처음 틀. 사진 안에 들어가는 **가장 큰** 비율 사각형을 사진 가운데 놓는다.
 *
 * 크게 시작하는 이유: 대개는 사진 전체를 쓰고 싶어 하고, 줄이는 것이
 * 키우는 것보다 쉽다.
 */
export function initialCrop(photo: Rect, aspect: number): Rect {
  const width = Math.min(photo.width, photo.height * aspect);
  const height = width / aspect;
  return {
    x: photo.x + (photo.width - width) / 2,
    y: photo.y + (photo.height - height) / 2,
    width,
    height,
  };
}

/**
 * 끌어 옮긴 사진의 새 자리.
 *
 * **틀은 언제나 사진으로 덮여 있어야 한다.** 사진을 너무 밀어 틀 안에 빈
 * 자리가 생기면 거기는 잘라 낼 화소가 없다 — 캔버스에 투명한 띠로 남고
 * jpeg로 내보내면 검게 나온다. 그래서 사진이 갈 수 있는 곳은
 * "틀의 한쪽 끝"과 "틀의 반대쪽 끝" 사이로 묶인다.
 */
export function clampPhoto(photo: Rect, crop: Rect): Rect {
  return {
    ...photo,
    x: clamp(photo.x, crop.x + crop.width - photo.width, crop.x),
    y: clamp(photo.y, crop.y + crop.height - photo.height, crop.y),
  };
}

/**
 * 모서리를 잡아 끌었을 때의 새 틀.
 *
 * **잡지 않은 쪽 모서리가 못이다.** 그 점을 고정하고 그 반대로만 자란다 —
 * 그래야 잡은 손과 반대쪽이 같이 움직이는 일이 없다.
 *
 * 비율이 잠겨 있으므로 가로·세로 중 **더 많이 끈 쪽**을 따라간다. 덜 끈 쪽만
 * 보면 대각선으로 끌 때 틀이 손을 못 따라온다.
 *
 * 사진 밖으로 나가지 않는 것은 크기로 막는다 — 자리를 나중에 밀어 넣으면
 * 잡은 모서리가 손에서 미끄러진다.
 */
export function resizeCrop(
  rect: Rect,
  corner: Corner,
  pointer: Point,
  photo: Rect,
  aspect: number,
  minWidth = MIN_CROP_WIDTH,
): Rect {
  const toEast = corner === 'ne' || corner === 'se';
  const toSouth = corner === 'se' || corner === 'sw';

  // 못이 되는 반대쪽 모서리
  const anchorX = toEast ? rect.x : rect.x + rect.width;
  const anchorY = toSouth ? rect.y : rect.y + rect.height;

  // 못에서 사진 가장자리까지 남은 자리
  const roomX = toEast ? photo.x + photo.width - anchorX : anchorX - photo.x;
  const roomY = toSouth ? photo.y + photo.height - anchorY : anchorY - photo.y;
  const maxWidth = Math.max(minWidth, Math.min(roomX, roomY * aspect));

  const draggedX = Math.abs(pointer.x - anchorX);
  const draggedY = Math.abs(pointer.y - anchorY);
  const width = Math.min(maxWidth, Math.max(minWidth, Math.max(draggedX, draggedY * aspect)));
  const height = width / aspect;

  return {
    x: toEast ? anchorX : anchorX - width,
    y: toSouth ? anchorY : anchorY - height,
    width,
    height,
  };
}

/**
 * 화면 위의 틀이 **원본 사진의 어디인지**.
 *
 * 이 사각형을 그대로 `drawImage`의 원본 영역으로 쓴다. 사진이 판 위 어디에
 * 놓였는지를 먼저 빼고(`crop - photo`) 배율로 나눈다 — 회전이 없으니 그게
 * 전부다.
 *
 * 마지막에 사진 안으로 한 번 더 가둔다 — 소수점 오차로 한 픽셀만 넘어가도
 * 캔버스에 투명한 줄이 생기고, jpeg로 내보내면 그게 검게 나온다.
 */
export function toSourceRect(crop: Rect, photo: Rect, scale: number, image: Size): Rect {
  const x = clamp((crop.x - photo.x) / scale, 0, image.width);
  const y = clamp((crop.y - photo.y) / scale, 0, image.height);
  return {
    x,
    y,
    width: Math.min(crop.width / scale, image.width - x),
    height: Math.min(crop.height / scale, image.height - y),
  };
}

/**
 * 잘라 낸 결과를 몇 픽셀로 내보낼지.
 *
 * 비율을 유지하면서 긴 변을 `maxEdge`로 맞춘다. 원본이 그보다 작으면
 * **늘리지 않는다** — 없는 화소를 지어내 봐야 용량만 는다.
 */
export function outputSize(aspect: number, source: Rect, maxEdge: number): Size {
  const longest = Math.min(maxEdge, Math.max(source.width, source.height));
  const width = aspect >= 1 ? longest : longest * aspect;
  const height = aspect >= 1 ? longest / aspect : longest;
  return { width: Math.max(1, Math.round(width)), height: Math.max(1, Math.round(height)) };
}
